<template>
  <a
    v-bind="linkBindings"
    :href="href"
    :target="target"
    :rel="target === '_blank' ? rel : undefined"
    role="link"
    tabindex="0"
  >
    <slot></slot>
  </a>
</template>

<script lang="ts" setup>
import { computed, mergeProps, useAttrs } from 'vue';

import { useLazyHref } from '@/composables/useLazyHref';

defineOptions({
  name: 'MaskedLink',
  inheritAttrs: false,
});

interface Props {
  /** Link target. Kept in JS memory only, never rendered as href for bots. */
  url: string;
  /**
   * When true (default false), the href is only set once the user shows
   * interaction intent (hover, focus, pointer/touch, right-click), so
   * crawlers never see the target URL while users keep full link behaviour.
   */
  masked?: boolean;
  target?: string;
  rel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  masked: false,
  target: '_blank',
  rel: 'noopener',
});

const attrs = useAttrs();

const { href: lazyHref, intentHandlers } = useLazyHref(() => props.url);

const href = computed((): string | undefined => (props.masked ? lazyHref.value : props.url));

const linkBindings = computed(() =>
  props.masked ? mergeProps({ ...intentHandlers }, attrs) : attrs,
);
</script>
